import _ from 'lodash';

// All draft mutations use the same write/clear path, including reconciliation.
export function createSettingsDraftStore(parent, tabId, persist) {
  return {
    read: () => parent.temp_settings[tabId] ?? {},
    write: (draft) => {
      if (_.isEmpty(draft)) delete parent.temp_settings[tabId];
      else parent.temp_settings[tabId] = _.cloneDeep(draft);
      persist();
    },
    clear() {
      this.write({});
    }
  };
}

export function restoreSettingsDraft(baseline, draft, customizer) {
  const settings = _.cloneDeep(draft);
  const deleted = settings._deleted_indicies ?? {};
  delete settings._deleted_indicies;
  const restored = customizer
    ? _.mergeWith(_.cloneDeep(baseline), settings, customizer)
    : _.merge(_.cloneDeep(baseline), settings);
  for (const [path, indexes] of Object.entries(deleted)) {
    const array = path === '' ? restored : _.get(restored, path);
    if (Array.isArray(array)) indexes.forEach((index) => array.splice(index, 1));
  }
  return restored;
}

export function createNestedSettingsDraft(store) {
  return {
    ...store,
    edit(entries) {
      let next = _.cloneDeep(store.read());
      next._deleted_indicies ??= {};
      entries.forEach(([path, value]) => {
        if (path === null) next = _.cloneDeep(value);
        else _.set(next, path, _.cloneDeep(value));
      });
      store.write(next);
    },
    remove(paths) {
      const next = _.cloneDeep(store.read());
      paths.forEach((path) => _.unset(next, path));
      store.write(next);
    },
    restore: (baseline) => restoreSettingsDraft(baseline, store.read())
  };
}

const containsPath = (parent, child) => {
  const prefix = _.toPath(parent);
  const path = _.toPath(child);
  return prefix.length <= path.length && prefix.every((part, index) => part === path[index]);
};

export function applyFieldDraft(baseline, fields) {
  const values = _.cloneDeep(baseline);
  Object.entries(fields).forEach(([path, value]) => _.set(values, path, _.cloneDeep(value)));
  return values;
}

export function pruneFieldDraft(fields, baseline, previousBaseline) {
  // When tracking readback, an unchanged cached value is not confirmation.
  return Object.fromEntries(
    Object.entries(fields).filter(
      ([path, value]) =>
        !_.isEqual(value, _.get(baseline, path)) ||
        (previousBaseline !== undefined &&
          _.isEqual(_.get(baseline, path), _.get(previousBaseline, path)))
    )
  );
}

export function updateFieldDraft(fields, entries, baseline, preserveUntouched = false) {
  const next = _.cloneDeep(fields);
  entries.forEach(([path, value]) => {
    // Replacing an array/object supersedes its old child edits. A later child
    // edit belongs inside an existing replacement, preserving its array length.
    Object.keys(next).forEach((key) => {
      if (containsPath(path, key)) delete next[key];
    });
    const ancestor = Object.keys(next).find((key) => containsPath(key, path));
    if (ancestor) {
      _.set(next[ancestor], _.toPath(path).slice(_.toPath(ancestor).length), _.cloneDeep(value));
    } else next[path] = _.cloneDeep(value);
  });
  // Without a baseline this only combines overrides, including parent/child
  // edits. Reconciliation is a separate operation once readback has arrived.
  if (baseline === undefined) return next;
  // An edit/reset may retire its own fields without acknowledging other edits
  // restored against an unchanged cached baseline.
  return Object.fromEntries(
    Object.entries(next).filter(
      ([path, value]) =>
        (preserveUntouched &&
          !entries.some(([edited]) => containsPath(edited, path) || containsPath(path, edited))) ||
        !_.isEqual(value, _.get(baseline, path))
    )
  );
}

export function createFieldSettingsDraft(store, getBaseline, preserveUntouched = false) {
  return {
    ...store,
    edit: (entries) =>
      store.write(updateFieldDraft(store.read(), entries, getBaseline(), preserveUntouched)),
    remove: (paths) => {
      const baseline = getBaseline();
      store.write(
        updateFieldDraft(
          store.read(),
          paths.map((path) => [path, _.get(baseline, path)]),
          baseline,
          preserveUntouched
        )
      );
    },
    restore: (baseline) => applyFieldDraft(baseline, store.read()),
    reconcile: (baseline, previousBaseline) => {
      const previous = store.read();
      const next = pruneFieldDraft(previous, baseline, previousBaseline);
      if (!_.isEqual(previous, next)) store.write(next);
    }
  };
}
